import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Panel } from '@cloudsam/ui';
import { resolveAnswerType, isOrdinal, ANSWER_TYPE_META, extractValues, extractAnswerRows } from './analysis/answerTypes';
import BooleanCanvas from './analysis/BooleanCanvas';
import SetMemberCanvas from './analysis/SetMemberCanvas';
import ScalarCanvas from './analysis/ScalarCanvas';
import FileUploadCanvas from './analysis/FileUploadCanvas';
import TextStubCard from './analysis/TextStubCard';
import ClassifiedTextCanvas from './analysis/ClassifiedTextCanvas';
import { TEXT_CLASSIFICATION } from '../utils/flags';
import SurveyHealthView from './analysis/SurveyHealthView';
import { exportChartPng, exportRowsCsv, buildFilename } from '../utils/exportUtils';
import AnalysisComparePanel from './analysis/AnalysisComparePanel';

const menuItemStyle = { display: 'block', width: '100%', padding: '8px 14px', border: 'none', background: 'transparent', color: '#202124', fontSize: 13, textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font-family-primary)' };

// The author's own reference for a question ("Q4"). Authors cite questions by
// that number, so every place this tab names a question leads with it. It is
// authored freely and can be absent, in which case the variable name stands
// alone rather than a placeholder taking its place.
export const authoredNumber = (question) => {
  const number = question?.question_number;
  return number === undefined || number === null || String(number).trim() === ''
    ? null
    : String(number).trim();
};

const normalizeBooleanResponse = (value) => {
  const lower = String(value).toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(lower) || value === true || value === 1) return 'Yes';
  if (['no', 'n', 'false', '0'].includes(lower) || value === false || value === 0) return 'No';
  return 'Other';
};

// Pinned Survey-health views (WP-A3): the three former Survey Performance
// charts, recast as system "questions" at the top of the Survey health group.
// They render the existing performance/* components against the same
// fetchPerformance endpoint, so their numbers match the old tab by construction.
const PINNED_HEALTH = [
  { name: '__health_duration', label: 'Time to complete', healthView: 'duration', special: true, pinned: true },
  { name: '__health_completion', label: 'Completion by question', healthView: 'completion', special: true, pinned: true },
  { name: '__health_dropoff', label: 'Drop-off funnel', healthView: 'dropoff', special: true, pinned: true },
  { name: '__health_timing', label: 'Responses over time', healthView: 'timing', special: true, pinned: true },
];

const HEALTH_TAG = { label: 'Survey health', bg: '#EEF2F6', fg: '#5F6368' };

// The question in the bar is the single question context for this deployment.
// Describe and Compare are subtabs of it, so Compare keeps no question list or
// selection of its own; it reads the same `selected` variable and sends its
// descriptor `question_id`.  Survey-health views are client-only Describe
// surfaces and are not questions Compare can request.
const compareQuestionFor = (question) => {
  if (!question || question.special || question.pinned || !question.id) return null;
  return {
    question_id: question.id,
    variable_name: question.name,
    text: question.label || question.name,
    question_type: question.type,
    authored_number: question.question_number,
    authored_number_unavailable: question.question_number === undefined || question.question_number === null || question.question_number === '',
  };
};

const Tag = ({ type }) => {
  const meta = ANSWER_TYPE_META[type] || { label: type, bg: '#E8EAED', fg: '#202124' };
  return (
    <span style={{
      backgroundColor: meta.bg,
      color: meta.fg,
      padding: '2px 8px',
      borderRadius: 4,
      fontSize: 11,
      fontWeight: 600,
      display: 'inline-block',
      whiteSpace: 'nowrap'
    }}>
      {meta.label}
    </span>
  );
};

export default function AnalysisTab({
  variables = [],
  responses = [],
  loading = false,
  deploymentId,
  healthPreselect,
  openQuestion,
  onQuestionOpened,
  onStructureRefresh,
  onOpenSession,
  // Held by the shell, which stays mounted while the tabs change. Without it
  // the question an author was reading is lost the moment they follow a link
  // into Explore, and coming back means starting the search over.
  analysisMemory,
  onAnalysisMemoryChange
}) {
  const [selected, setSelected] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewType, setViewType] = useState('');
  const [showProportion, setShowProportion] = useState(false);
  const [binCount, setBinCount] = useState('');
  const [scalarExportOpen, setScalarExportOpen] = useState(false);
  const [booleanExportOpen, setBooleanExportOpen] = useState(false);
  const [fileExportOpen, setFileExportOpen] = useState(false);
  const [fileExportActions, setFileExportActions] = useState(null);
  // A free-text question only draws a chart once its answers are categorized,
  // so the canvas reports when one is on screen and the bar-orientation
  // control appears with it rather than sitting dead above an empty state.
  const [textChartReady, setTextChartReady] = useState(false);
  const [analysisLevel, setAnalysisLevel] = useState(() => analysisMemory?.level || 'describe');
  const [describeSelections, setDescribeSelections] = useState(() => analysisMemory?.selections || {});
  const [selectedDescribeDeploymentId, setSelectedDescribeDeploymentId] = useState(null);
  const [compareMappings, setCompareMappings] = useState([]);
  const [wholeSurveyMode, setWholeSurveyMode] = useState(false);
  const [abNavigationRows, setAbNavigationRows] = useState([]);
  const [abFocus, setAbFocus] = useState({ key: 'all', label: 'All questions' });
  const scalarChartRef = useRef(null);
  const booleanChartRef = useRef(null);

  // WP-D3: a refreshKey the text canvas keys its classification reload on.
  const [classifyRefreshKey] = useState(0);

  // WP-D3b: categories were appended (discovery adopt / in-flow create) since
  // the last classify — the canvas shows a "Re-classify to apply" hint.
  const [classifyStaleHint, setClassifyStaleHint] = useState(false);

  // Portal target for the discovery panel's Save card, so it renders in the
  // right rail under the Summary box (static) while the cluster list scrolls.
  // A callback ref stored in state re-renders once the slot DOM node mounts.
  const [saveSlotNode, setSaveSlotNode] = useState(null);

  // WP-D3b: `selected` holds the variable object captured at selection time;
  // after a structure refresh (category adoption) the rebuilt `variables`
  // carry the fresh responseCategories, but `selected` would keep the stale
  // object. Resolve the LIVE same-name variable for the classified-text
  // surfaces without touching selection state (no viewType/panel resets).
  const selectedLive = useMemo(() => {
    if (!selected || selected.pinned || selected.special) return selected;
    return variables.find(v => v.name === selected.name) || selected;
  }, [selected, variables]);

  // Push the question memory up so it survives this tab unmounting.
  useEffect(() => {
    onAnalysisMemoryChange?.({ selections: describeSelections, level: analysisLevel });
  }, [describeSelections, analysisLevel, onAnalysisMemoryChange]);

  const dropdownRef = useRef(null);
  const selectDescribe = useCallback((question) => {
    setSelected(question);
    setSelectedDescribeDeploymentId(deploymentId);
    if (question) {
      setDescribeSelections(previous => previous[deploymentId]?.name === question.name ? previous : { ...previous, [deploymentId]: question });
    }
  }, [deploymentId]);

  const compareQuestion = useMemo(() => compareQuestionFor(selected), [selected]);

  // The question memory is per deployment and shared by both subtabs, so
  // switching between Describe and Compare never disturbs it.
  useEffect(() => {
    if (!deploymentId) return;
    const remembered = describeSelections[deploymentId];
    if (remembered && remembered.name !== selected?.name) selectDescribe(remembered);
    if (!remembered && selectedDescribeDeploymentId !== deploymentId) setSelected(null);
  }, [deploymentId, describeSelections, selectedDescribeDeploymentId, selectDescribe, selected]);

  // Auto-select on mount / variables change so a result always renders.
  // When arriving from ?tab=performance (healthPreselect), land on the first
  // pinned Survey-health view (WP-A1 acceptance) instead of the first question.
  useEffect(() => {
    if (selected) return;
    // A remembered question wins over the default. Both effects run in the
    // same commit with `selected` still null, so without this the restore
    // lands and is immediately overwritten by the first question — which is
    // what threw an author back to Q1 on returning from Explore.
    if (describeSelections[deploymentId]) return;
    if (healthPreselect) {
      selectDescribe(PINNED_HEALTH[0]);
      return;
    }
    if (variables && variables.length > 0) {
      // Identifier variables (e.g. email) aren't analyzable — skip them.
      const firstNonSpecial = variables.find(v => !v.special && v.matrixType !== 'identifier');
      selectDescribe(firstNonSpecial || variables.find(v => v.matrixType !== 'identifier') || null);
    }
  }, [variables, selected, healthPreselect, selectDescribe, describeSelections, deploymentId]);

  // Open-in-Analysis bridge (WP-C2): when the Explore matrix asks to open a
  // question, select it here, then clear the signal.
  useEffect(() => {
    if (openQuestion && variables) {
      const match = variables.find(v => v.name === openQuestion);
      if (match) selectDescribe(match);
      onQuestionOpened?.();
    }
  }, [openQuestion, variables]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset the Describe view controls when the selected question changes.  The
  // Describe/Compare subtab is a persistent view of the one question context,
  // so changing question — or deployment — never throws the user back to
  // Describe.
  useEffect(() => {
    if (selected) {
      const at = resolveAnswerType(selected);
      if (at === 'boolean') {
        setViewType('cards');
      } else if (at === 'setMember') {
        setViewType('bar');
      } else if (at === 'scalar') {
        setViewType('histogram');
      } else if (at === 'text') {
        setViewType('vertical-bars');
      } else {
        setViewType('');
      }
      setTextChartReady(false);
      setBinCount('');
      setShowProportion(false);
      setScalarExportOpen(false);
      setBooleanExportOpen(false);
      setFileExportOpen(false);
      setFileExportActions(null);
    }
  }, [selected]);

  // Click outside and Escape key listeners to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Filtered variables for dropdown
  const nonSpecialFiltered = useMemo(() => {
    // Exclude identifier variables (e.g. email) — they aren't analyzable and
    // would otherwise render as a mislabeled Text question.
    const list = (variables || []).filter(v => !v.special && v.matrixType !== 'identifier');
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    // The authored number is searchable too — it is what an author reaches for
    // first ("show me Q4").
    return list.filter(v =>
      (v.name && v.name.toLowerCase().includes(q)) ||
      (v.label && v.label.toLowerCase().includes(q)) ||
      (authoredNumber(v) || '').toLowerCase().includes(q)
    );
  }, [variables, searchQuery]);

  const specialFiltered = useMemo(() => {
    // Pinned Survey-health views sit at the top of the health group, ahead of
    // the plain system variables.
    const list = [...PINNED_HEALTH, ...(variables || []).filter(v => !!v.special)];
    if (!searchQuery) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(v =>
      (v.name && v.name.toLowerCase().includes(q)) ||
      (v.label && v.label.toLowerCase().includes(q))
    );
  }, [variables, searchQuery]);

  // Whole-survey rows are server-owned report navigation. Filtering this list
  // only changes the selector menu; it never filters, reorders, or re-queries
  // the report itself.
  const wholeSurveyNavigationRows = useMemo(() => {
    if (!searchQuery) return abNavigationRows;
    const needle = searchQuery.toLowerCase();
    return abNavigationRows.filter(row => [row.variable_name, row.label, row.status, row.reason]
      .some(value => String(value || '').toLowerCase().includes(needle)));
  }, [abNavigationRows, searchQuery]);

  // Extract values for currently selected variable, and the same answers with
  // their respondent still attached — clicking a bar has to name names.
  const answerRows = useMemo(() => {
    if (!selected || selected.pinned) return [];
    return extractAnswerRows(responses || [], selected.name);
  }, [responses, selected]);

  const values = useMemo(() => answerRows.map(row => row.value), [answerRows]);

  const selectedType = selected ? (selected.pinned ? 'health' : resolveAnswerType(selected)) : null;

  const scalarExportRows = useMemo(() => {
    if (!selected || selectedType !== 'scalar') return [];
    return (responses || []).reduce((rows, response) => {
      const value = response?.[selected.name];
      if (value == null || value === '') return rows;
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return rows;
      rows.push({
        recipientId: response?.recipient_id ?? '',
        value: numericValue,
        sessionId: response?.session_id ?? '',
      });
      return rows;
    }, []);
  }, [responses, selected, selectedType]);

  const scalarExportColumns = useMemo(() => ([
    { header: 'Recipient ID', get: (row) => row.recipientId },
    { header: 'Response value', get: (row) => row.value },
    { header: 'Session ID', get: (row) => row.sessionId },
  ]), []);

  const exportScalarData = () => {
    if (!selected) return;
    exportRowsCsv(
      scalarExportRows,
      scalarExportColumns,
      buildFilename(selected.label || selected.name || 'question', deploymentId, 'scalar-data', 'csv')
    );
  };

  const exportScalarChart = () => {
    if (!selected) return;
    exportChartPng(
      scalarChartRef,
      buildFilename(selected.label || selected.name || 'question', deploymentId, 'scalar-chart', 'png')
    );
  };

  const booleanExportRows = useMemo(() => {
    if (!selected || selectedType !== 'boolean') return [];
    return (responses || []).reduce((rows, response) => {
      const value = response?.[selected.name];
      if (value == null || value === '') return rows;
      rows.push({
        recipientId: response?.recipient_id ?? '',
        response: normalizeBooleanResponse(value),
        sessionId: response?.session_id ?? '',
      });
      return rows;
    }, []);
  }, [responses, selected, selectedType]);

  const booleanExportColumns = useMemo(() => ([
    { header: 'Recipient ID', get: (row) => row.recipientId },
    { header: 'Response', get: (row) => row.response },
    { header: 'Session ID', get: (row) => row.sessionId },
  ]), []);

  const exportBooleanData = () => {
    if (!selected) return;
    exportRowsCsv(
      booleanExportRows,
      booleanExportColumns,
      buildFilename(selected.label || selected.name || 'question', deploymentId, 'boolean-data', 'csv')
    );
  };

  const exportBooleanChart = () => {
    if (!selected) return;
    exportChartPng(
      booleanChartRef,
      buildFilename(selected.label || selected.name || 'question', deploymentId, 'boolean-chart', 'png')
    );
  };

  const renderGroup = (title, items) => {
    if (items.length === 0) return null;
    return (
      <div key={title}>
        <div style={{
          padding: '8px 12px 4px 12px',
          fontSize: 11,
          fontWeight: 600,
          color: '#5F6368',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          backgroundColor: '#F8F9FA'
        }}>
          {title}
        </div>
        {items.map(item => {
          const itemValues = item.pinned ? null : extractValues(responses || [], item.name);
          const itemType = item.pinned ? null : resolveAnswerType(item);
          return (
                <div
                  key={item.name}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    selectDescribe(item);
                    setDropdownOpen(false);
                    setSearchQuery('');
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectDescribe(item);
                      setDropdownOpen(false);
                      setSearchQuery('');
                    }
                  }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #F1F3F4',
                transition: 'background 0.1s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F1F3F4'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0, marginRight: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {!item.pinned && authoredNumber(item) && (
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1558B0' }}>{authoredNumber(item)}</span>
                  )}
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#202124' }}>{item.pinned ? item.label : item.name}</span>
                  {item.pinned ? (
                    <span style={{ backgroundColor: HEALTH_TAG.bg, color: HEALTH_TAG.fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>Pinned view</span>
                  ) : (
                    <Tag type={itemType} />
                  )}
                </div>
                {!item.pinned && (
                  <div style={{
                    fontSize: 12,
                    color: '#5F6368',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {item.label || item.name}
                  </div>
                )}
              </div>
              {!item.pinned && (
                <div style={{ fontSize: 11, color: '#5F6368', flexShrink: 0 }}>
                  {itemValues.length} / {(responses || []).length}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const selectAbFocus = (focus) => {
    setAbFocus(focus);
    setDropdownOpen(false);
    setSearchQuery('');
    document.getElementById('analysis-whole-survey-report')?.focus();
  };

  const renderViewSwitcher = () => {
    if (!selected || selected.pinned) return null;

    let options = [];
    if (selectedType === 'text') {
      // Category names are long sentences. Vertical bars angle them into
      // truncation; horizontal bars give each one a full line to read.
      if (!textChartReady) return null;
      options = [
        { value: 'vertical-bars', label: 'Vertical bars' },
        { value: 'horizontal-bars', label: 'Horizontal bars' }
      ];
    } else if (selectedType === 'boolean') {
      options = [
        { value: 'cards', label: 'Cards' },
        { value: 'pie', label: 'Pie' }
      ];
    } else if (selectedType === 'setMember') {
      options = [
        { value: 'bar', label: 'Bar' },
        { value: 'pie', label: 'Pie' },
        { value: 'table', label: 'Table' }
      ];
    } else if (selectedType === 'scalar') {
      options = [
        { value: 'histogram', label: 'Histogram' },
        { value: 'horizontal-bars', label: 'Horizontal bars' }
      ];
    }

    if (options.length === 0) return null;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {selectedType === 'scalar' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12, color: '#5F6368' }}>Bins:</span>
            <input
              type="number"
              min="3"
              max="50"
              value={binCount || ''}
              onChange={(e) => {
                const val = e.target.value;
                setBinCount(val === '' ? '' : Math.max(3, Math.min(50, Number(val))));
              }}
              placeholder="Auto"
              style={{
                width: 60,
                padding: '4px 6px',
                fontSize: 12,
                border: '1px solid #DADCE0',
                borderRadius: 4,
                outline: 'none'
              }}
            />
          </div>
        )}

        {selectedType === 'setMember' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#5F6368', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showProportion}
              onChange={(e) => setShowProportion(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Proportion
          </label>
        )}

        <div style={{ display: 'flex', gap: 4 }}>
          {options.map(opt => {
            const isActive = viewType === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setViewType(opt.value)}
                style={{
                  fontSize: 12,
                  padding: '4px 10px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.15s',
                  backgroundColor: isActive ? '#EBF4FF' : '#FFFFFF',
                  color: isActive ? '#2B6BBF' : '#5F6368',
                  border: isActive ? '1px solid #8DBAFF' : '1px solid #DADCE0',
                  fontWeight: isActive ? 600 : 400
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAnalysisExport = () => {
    const isScalar = selectedType === 'scalar';
    const isBoolean = selectedType === 'boolean';
    const isFileUpload = selectedType === 'fileUpload';
    if (!isScalar && !isBoolean && !isFileUpload) return null;

    const open = isScalar ? scalarExportOpen : (isBoolean ? booleanExportOpen : fileExportOpen);
    const setOpen = isScalar ? setScalarExportOpen : (isBoolean ? setBooleanExportOpen : setFileExportOpen);
    const menuId = isScalar ? 'scalar-analysis-export-menu' : (isBoolean ? 'boolean-analysis-export-menu' : 'file-upload-export-menu');

    return (
      <div style={{ position: 'relative' }}>
        <button
          onClick={() => setOpen(current => !current)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={menuId}
          disabled={isFileUpload && !fileExportActions}
          style={{ padding: '4px 10px', fontSize: 12, color: '#3C4043', backgroundColor: '#FFFFFF', border: '1px solid #DADCE0', borderRadius: 4, cursor: isFileUpload && !fileExportActions ? 'wait' : 'pointer', outline: 'none' }}
        >Export ▾</button>
        {open && (
          <>
            <div aria-hidden="true" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
            <div id={menuId} role="menu" aria-label="Export analysis" style={{ position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100, minWidth: 160, padding: '4px 0', background: '#FFFFFF', border: '1px solid #DADCE0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
              {isFileUpload ? <>
                <button role="menuitem" disabled={fileExportActions?.isExporting} style={menuItemStyle} onClick={() => { setOpen(false); fileExportActions?.exportOne(); }}>One</button>
                <button role="menuitem" disabled={fileExportActions?.isExporting} style={menuItemStyle} onClick={() => { setOpen(false); fileExportActions?.exportSelected(); }}>Selected</button>
                <button role="menuitem" disabled={fileExportActions?.isExporting} style={menuItemStyle} onClick={() => { setOpen(false); fileExportActions?.exportAllMatching(); }}>All matching</button>
              </> : <>
                <button role="menuitem" style={menuItemStyle} onClick={() => { setOpen(false); (isScalar ? exportScalarData : exportBooleanData)(); }}>Data (CSV)</button>
                <button role="menuitem" style={menuItemStyle} onClick={() => { setOpen(false); (isScalar ? exportScalarChart : exportBooleanChart)(); }}>Chart (PNG)</button>
              </>}
            </div>
          </>
        )}
      </div>
    );
  };

  const renderDescribeCommands = () => {
    const switcher = renderViewSwitcher();
    const exportMenu = renderAnalysisExport();
    if (!switcher && !exportMenu) return null;
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12, flexWrap: 'wrap' }}>{switcher}{exportMenu}</div>;
  };

  // §5.2 design intent: show the question's authored parameters beneath the
  // title so the analysis is read against how the recipient experienced it —
  // most importantly the set-member option list, in authored order, which is
  // the chart's category order.
  const renderDesignIntent = () => {
    if (!selected || selected.pinned) return null;
    let text = null;
    if (selectedType === 'setMember' && (selected.options || []).length) {
      text = `Options: ${selected.options.join(' · ')}`;
    } else if (selectedType === 'boolean') {
      text = 'Answer: Yes / No';
    } else if (selectedType === 'scalar') {
      text = 'Numeric response';
    } else if (selectedType === 'text') {
      text = 'Free-text response';
    }
    if (!text) return null;
    return (
      <div style={{ fontSize: 12, color: '#5F6368', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={text}>
        {text}
      </div>
    );
  };

  const renderCanvas = () => {
    if (!selected) return null;
    if (selected.pinned) {
      return <SurveyHealthView view={selected.healthView} deploymentId={deploymentId} responses={responses} />;
    }
    switch (selectedType) {
      case 'boolean':
        return <BooleanCanvas values={values} view={viewType || 'cards'} chartRef={booleanChartRef} rows={answerRows} onOpenSession={onOpenSession} />;
      case 'setMember':
        return (
          <SetMemberCanvas
            values={values}
            options={selected.options || []}
            isOrdinal={isOrdinal(selected)}
            view={viewType || 'bar'}
            showProportion={showProportion}
            rows={answerRows}
            onOpenSession={onOpenSession}
          />
        );
      case 'scalar':
        return (
          <ScalarCanvas
            values={values}
            binCount={binCount ? Number(binCount) : undefined}
            view={viewType || 'histogram'}
            chartRef={scalarChartRef}
            rows={answerRows}
            onOpenSession={onOpenSession}
          />
        );
      case 'fileUpload':
        return <FileUploadCanvas question={selected} deploymentId={deploymentId} responses={responses} onExportActionsChange={setFileExportActions} />;
      case 'text':
        return TEXT_CLASSIFICATION
          ? <ClassifiedTextCanvas
              question={selectedLive}
              deploymentId={deploymentId}
              responses={responses}
              refreshKey={classifyRefreshKey}
              onStructureRefresh={onStructureRefresh}
              staleHint={classifyStaleHint}
              onStaleHintClear={() => setClassifyStaleHint(false)}
              saveSlot={saveSlotNode}
              onOpenSession={onOpenSession}
              view={viewType || 'vertical-bars'}
              onChartReadyChange={setTextChartReady}
            />
          : <TextStubCard values={values} count={values.length} />;
      default:
        return null;
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      overflow: 'auto',
      padding: 16,
      background: 'var(--color-bg-base)',
      fontFamily: 'var(--font-family-primary, sans-serif)',
      boxSizing: 'border-box',
      height: '100%'
    }}>
      {/* Question Breadcrumb Row */}
      <div style={{ marginBottom: 16, position: 'relative' }} ref={dropdownRef}>
        <div style={{ fontSize: 11, letterSpacing: '0.08em', color: '#5F6368', fontWeight: 600, marginBottom: 6 }}>
          QUESTION
        </div>
        <button
          onClick={() => setDropdownOpen(!dropdownOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: '#FFFFFF',
            border: '1px solid #DADCE0',
            borderRadius: 20,
            padding: '6px 14px',
            cursor: 'pointer',
            outline: 'none',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            maxWidth: '100%',
            textAlign: 'left'
          }}
        >
          {analysisLevel === 'compare' && wholeSurveyMode ? (
            <span style={{ fontSize: 13, color: '#202124', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{abFocus.label}</span>
          ) : selected ? (
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {authoredNumber(selected) && <>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1558B0', flexShrink: 0 }}>
                  {authoredNumber(selected)}
                </span>
                <span style={{ fontSize: 13, color: '#5F6368', flexShrink: 0 }}>·</span>
              </>}
              <span style={{ fontSize: 13, fontWeight: 600, color: '#202124', flexShrink: 0 }}>
                {selected.name}
              </span>
              <span style={{ fontSize: 13, color: '#5F6368', flexShrink: 0 }}>·</span>
              <span style={{
                fontSize: 13,
                color: '#5F6368',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {selected.label || selected.name}
              </span>
            </span>
          ) : (
            <span style={{ fontSize: 13, color: '#5F6368' }}>Select a question...</span>
          )}
          <span style={{ fontSize: 10, color: '#5F6368', marginLeft: 4, flexShrink: 0 }}>▼</span>
        </button>

        {dropdownOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 1000,
            width: 480,
            maxWidth: '100vw',
            background: '#FFFFFF',
            border: '1px solid #DADCE0',
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            marginTop: 4,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 400,
            overflow: 'hidden'
          }}>
            <div style={{ padding: 8, borderBottom: '1px solid #E8EAED' }}>
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  fontSize: 13,
                  border: '1px solid #DADCE0',
                  borderRadius: 4,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
              {analysisLevel === 'compare' && wholeSurveyMode ? <>
                <button type="button" onClick={() => selectAbFocus({ key: 'all', label: 'All questions' })} style={menuItemStyle}>All questions</button>
                {wholeSurveyNavigationRows.map((row, index) => <button type="button" key={row.cursor || `${row.variable_name}-${index}`} onClick={() => selectAbFocus({ key: row.cursor || `${row.variable_name}-${index}`, label: row.label || row.variable_name || 'Question' })} style={menuItemStyle}>{row.label || row.variable_name}{row.status ? ` · ${row.status}` : ''}{row.reason ? ` · ${row.reason}` : ''}</button>)}
                {wholeSurveyNavigationRows.length === 0 && <div style={{ padding: 16, textAlign: 'center', color: '#5F6368', fontSize: 13 }}>No questions match your search.</div>}
              </> : <>
                {renderGroup("Survey questions", nonSpecialFiltered)}
                {renderGroup("Survey health", specialFiltered)}
                {nonSpecialFiltered.length === 0 && specialFiltered.length === 0 && (
                <div style={{ padding: '16px', textAlign: 'center', color: '#5F6368', fontSize: 13 }}>
                  No questions match your search.
                </div>
                )}
              </>}
            </div>
          </div>
        )}
      </div>

      {/* Result Card. Describe fits the viewport and scrolls its own canvas
          column. Compare instead grows to its content and lets the page scroll,
          so the chart, evidence and per-Segment detail are reached with the one
          window scrollbar rather than a pane nested inside the card. */}
      <Panel
        title={selected ? (selected.label || selected.name) : undefined}
        /* The question is the heading for this region — keep it a real <h2>
           so the page retains its heading outline. */
        titleAs="h2"
        actions={selected ? (
          <>
            {selected.pinned ? (
              <span style={{ backgroundColor: HEALTH_TAG.bg, color: HEALTH_TAG.fg, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{HEALTH_TAG.label}</span>
            ) : (
              <Tag type={selectedType} />
            )}
            {analysisLevel === 'describe' && renderDescribeCommands()}
          </>
        ) : null}
        style={{
          overflow: analysisLevel === 'compare' ? 'visible' : 'hidden',
          flex: analysisLevel === 'compare' ? '0 0 auto' : 1,
        }}
        bodyStyle={{
          display: 'flex',
          flexDirection: 'column',
          overflow: analysisLevel === 'compare' ? 'visible' : 'hidden',
        }}
      >
        {/* The question's design intent reads as a subtitle under the panel
            head, where the h2 + tag row used to sit (§3: the panel head carries
            title, count and commands; descriptive text belongs in the body). */}
        {selected && renderDesignIntent() && (
          <div style={{ padding: '12px 16px 0' }}>{renderDesignIntent()}</div>
        )}

        <div role="tablist" aria-label="Analysis results" style={{ padding: '0 16px', borderBottom: '1px solid #E8EAED', display: 'flex', gap: 16, backgroundColor: '#FFFFFF' }}>
              {[
                ['Describe', 'describe', 'Summarize one question.'],
                ['Compare', 'compare', 'Compare Segment mappings.'],
              ].map(([label, level, title]) => {
                const active = analysisLevel === level;
                return (
                  <button
                    key={level}
                    role="tab"
                    aria-selected={active}
                    onClick={() => setAnalysisLevel(level)}
                    title={title}
                    style={{ padding: '12px 4px', fontSize: 13, fontWeight: active ? 600 : 500, color: active ? '#1967D2' : '#5F6368', border: 'none', background: 'none', borderBottom: active ? '2px solid #1967D2' : '2px solid transparent', cursor: 'pointer', outline: 'none' }}
                  >{label}</button>
                );
              })}
        </div>

        <div role="tabpanel" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, flex: analysisLevel === 'compare' ? '0 0 auto' : 1, minHeight: 0 }}>
          {analysisLevel === 'compare' ? (
            <AnalysisComparePanel
              deploymentId={deploymentId}
              selectedQuestion={compareQuestion}
              selectedMappings={compareMappings}
              onSelectedMappingsChange={setCompareMappings}
              onWholeSurveyChange={setWholeSurveyMode}
              onNavigationRows={setAbNavigationRows}
              abFocus={abFocus}
            />
          ) : selected ? (
                <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
                  <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                    {renderCanvas()}
                  </div>
                  {!selected.pinned && selectedType !== 'fileUpload' && (
                    <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, alignSelf: 'flex-start' }}>
                      <div style={{ backgroundColor: '#FAFBFC', border: '1px solid #E8EAED', borderRadius: 6, padding: 12, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#5F6368', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Summary</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ color: '#5F6368' }}>Answered</span><span style={{ color: '#202124', fontWeight: 500 }}>{values.length}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ color: '#5F6368' }}>Total responses</span><span style={{ color: '#202124', fontWeight: 500 }}>{(responses || []).length}</span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}><span style={{ color: '#5F6368' }}>Type</span><span style={{ color: '#202124', fontWeight: 500 }}>{ANSWER_TYPE_META[selectedType]?.label || selectedType}</span></div>
                      </div>
                      <div ref={setSaveSlotNode} />
                    </div>
                  )}
                </div>
          ) : (
            <div style={{
              padding: 48,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flex: 1,
              color: '#5F6368',
              fontSize: 15,
              textAlign: 'center'
            }}>
              {loading ? "Loading questions…" : "No questions available for this deployment."}
            </div>
          )}
        </div>
      </Panel>

    </div>
  );
}
